/**
 * Maps mutation keys to user-friendly success messages.
 * Used by the global MutationCache to show toast notifications.
 */

const successMessages: Record<string, string> = {
  // Release Cycles
  createReleaseCycle: "Release cycle created",
  updateReleaseCycle: "Release cycle updated",
  deleteReleaseCycle: "Release cycle deleted",
  updateReleaseCycleStatus: "Release cycle status updated",

  // Deployments
  createDeployment: "Deployment created",
  updateDeployment: "Deployment updated",
  deleteDeployment: "Deployment deleted",
  submitDeployment: "Deployment submitted for approval",
  executeDeployment: "Deployment started",
  updateDeploymentStatus: "Deployment status updated",

  // Release Notes Sections
  createSection: "Section added",
  updateSection: "Section updated",
  deleteSection: "Section deleted",
  reorderSections: "Sections reordered",

  // Release Notes Items
  createItem: "Item added",
  updateItem: "Item updated",
  deleteItem: "Item deleted",
  reorderItems: "Items reordered",

  // Services
  createService: "Service created",
  updateService: "Service updated",
  deleteService: "Service deleted",

  // Environments
  createEnvironment: "Environment created",
  updateEnvironment: "Environment updated",
  deleteEnvironment: "Environment deleted",

  // Servers
  createServer: "Server created",
  updateServer: "Server updated",
  deleteServer: "Server deleted",

  // Groups
  createGroup: "Group created",
  updateGroup: "Group updated",
  deleteGroup: "Group deleted",
  updateGroupWorkflowSettings: "Workflow settings updated",

  // Scans
  createScan: "Scan started",
  deleteScan: "Scan deleted",

  // Users
  createUser: "User created",
  updateUser: "User updated",
  deleteUser: "User deleted",
  resetPassword: "Password reset",

  // Roles
  createRole: "Role created",
  updateRole: "Role updated",
  deleteRole: "Role deleted",

  // Section Types
  createSectionType: "Section type created",
  updateSectionType: "Section type updated",
  deleteSectionType: "Section type deleted",

  // Release Note Templates
  createReleaseNoteTemplate: "Template created",
  updateReleaseNoteTemplate: "Template updated",
  deleteReleaseNoteTemplate: "Template deleted",
  setDefaultTemplate: "Default template set",

  // API Keys
  createApiKey: "API key created",
  deleteApiKey: "API key deleted",

  // Profile
  updateProfile: "Profile updated",
  changePassword: "Password changed",

  // Rollups
  generateRollups: "Rollup deployments generated",
};

/**
 * Get a user-friendly success message for a mutation key.
 * Returns null if no message is defined (toast will be skipped).
 */
export function getMutationSuccessMessage(key: string): string | null {
  return successMessages[key] || null;
}

/**
 * Extract error message from an API error response.
 * Handles various error response formats.
 */
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    // Axios error with response data
    const axiosError = error as {
      response?: { data?: { message?: string; error?: string } };
      message?: string;
    };

    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }

    if (axiosError.message) {
      return axiosError.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
}
